import React, { useEffect, useState } from 'react'
import { Container, Card, Row, Col, Button, Modal, Badge, Spinner } from 'react-bootstrap'
import { useParams, useHistory, Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
    faArrowLeft, 
    faEdit, 
    faFile, 
    faUser, 
    faEnvelope, 
    faPhone, 
    faCalendar, 
    faUsers, 
    faInfoCircle, 
    faTrash,
    faClock,
    faTag
} from '@fortawesome/free-solid-svg-icons'
import Utility from '../../../../GlobalModules/Utility/Utility'
import * as BookingEventsController from '../Controller/BookingEventsController'
import ModalGlobal from '../../../../GlobalModules/Utility/Components/ModalGlobal';
import ConfirmationAlert from '../../../../GlobalModules/Utility/Components/ConfirmationAlert'
import ErrorBoundary from '../../../../GlobalModules/Utility/Components/ErrorBoundary';
import '../../Quotation/Assets/css/quotation.scss'

const BookingView = (props) => {
  const { slack, lng } = useParams()
  const history = useHistory()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const res = await BookingEventsController.getBookingDetailApi(slack)
        if (res.success) {
          const bookingData = res.data?.data || res.data
          setData(bookingData)
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slack])

  const fmt = (v) => Utility.getSafeTimeFormat(v, '-')

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)
      const response = await BookingEventsController.deleteBookingApi(slack)
      if (response.success) {
        Utility.toastNotifications(
          'Booking deleted successfully',
          'Success',
          'success'
        )
        history.push(`/${lng}/booking-events`)
      } else {
        Utility.toastNotifications(
          response.message || 'Failed to delete booking',
          'Error',
          'error'
        )
      }
    } catch (error) {
      Utility.toastNotifications(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete booking',
        'Error',
        'error'
      )
    } finally {
      setDeleteLoading(false)
      setShowDeleteModal(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
  }

  // Get person count from various possible fields
  const getPersonCount = () => {
    if (!data) return '-'
    return data?.no_of_persons !== undefined && data?.no_of_persons !== null
      ? data.no_of_persons
      : data?.number_of_persons !== undefined && data?.number_of_persons !== null
      ? data.number_of_persons
      : data?.persons !== undefined && data?.persons !== null
      ? data.persons
      : data?.guests !== undefined && data?.guests !== null
      ? data.guests
      : data?.people !== undefined && data?.people !== null
      ? data.people
      : '-'
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const bookingViewPermissions = Utility.hasMultipleActionPermissions(
    'bookings_and_calendar',
    'bookings_events',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  // Render loading state
  if (loading) {
    return (
      <Container fluid className="p-0">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading booking details...</p>
        </div>
      </Container>
    )
  }

  if (!data) {
    return (
      <Container fluid className="p-0">
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faInfoCircle} size="3x" className="text-danger mb-3" />
          <h4>Booking not found</h4>
          <p>Failed to load booking details</p>
          <Link to={`/${lng}/booking-events`} className="btn btn-primary mt-3">
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to List
          </Link>
        </div>
      </Container>
    )
  }

  return (
    <ErrorBoundary title="BookingView Error">
      <Container fluid className='p-0 quotation-view-page'>
        <Row className='top-header-box quotation-view-header mb-3'>
          <Col className='quotation-header-content'>
            <div className='quotation-title-row'>
              <h4 className='quotation-title'>
                {data.event_code || 'Booking'}
              </h4>
              <Badge bg="primary" className='badgeCustom'>
                {data.event_type || 'Event'}
              </Badge>
            </div>
            {/* <div className='quotation-subtitle'>
              <span className='text-muted'>Booking Details</span>
            </div> */}
          </Col>
          <Col xs='auto' className='quotation-header-actions'>
            <Link 
              to={`/${lng}/booking-events`} 
              className='btn btn-outline-secondary me-2'
            >
              <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
              Back
            </Link>
            {bookingViewPermissions.edit && (
              <Link 
                to={`/${lng}/booking-events/booking/edit/${slack}`} 
                className='btn btn-outline-primary me-2'
              >
                <FontAwesomeIcon icon={faEdit} className='me-2' />
                Edit
              </Link>
            )}
            {bookingViewPermissions.delete && (
              <Button 
                variant='outline-danger'
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                <FontAwesomeIcon icon={faTrash} className='me-2' />
                Delete
              </Button>
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className='custom-border-light mb-4 main-content-card quotation-details-card'>
              <Card.Header>
                <h6><FontAwesomeIcon icon={faFile} className='me-2' /> Booking Information</h6>
              </Card.Header>
              <Card.Body>
                <div className='quotation-details-grid'>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faTag} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>Event Type</span>
                      <span className='detail-value'>{data.event_type || '-'}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faUser} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>Name</span>
                      <span className='detail-value'>{data.name || '-'}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faEnvelope} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>Email</span>
                      <span className='detail-value'>{data.email || '-'}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faPhone} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>Contact No</span>
                      <span className='detail-value'>{data.phone || '-'}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faUsers} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>No. of Persons</span>
                      <span className='detail-value'>{getPersonCount()}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faClock} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>Start Time</span>
                      <span className='detail-value'>{fmt(data.start_date)}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faClock} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>End Time</span>
                      <span className='detail-value'>{fmt(data.end_date)}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faCalendar} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>Created On</span>
                      <span className='detail-value'>{fmt(data.created_at)}</span>
                    </div>
                  </div>
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faUser} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>Created By</span>
                      <span className='detail-value'>{data.created_by || 'System'}</span>
                    </div>
                  </div>

                  {data.updated_at && data.created_at !== data.updated_at && (
                    <>
                      <div className='detail-item'>
                        <span className='detail-icon'><FontAwesomeIcon icon={faCalendar} /></span>
                        <div className='detail-content'>
                          <span className='detail-label'>Updated On</span>
                          <span className='detail-value'>{fmt(data.updated_at)}</span>
                        </div>
                      </div>
                      <div className='detail-item'>
                        <span className='detail-icon'><FontAwesomeIcon icon={faUser} /></span>
                        <div className='detail-content'>
                          <span className='detail-label'>Updated By</span>
                          <span className='detail-value'>{data.updated_by || 'System'}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {data.description && (
                    <div className='detail-item'>
                      <span className='detail-icon'><FontAwesomeIcon icon={faInfoCircle} /></span>
                      <div className='detail-content'>
                        <span className='detail-label'>Description</span>
                        <span className='detail-value'>{data.description || '-'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Delete Confirmation Modal using ConfirmationAlert */}
        <ModalGlobal
          show={showDeleteModal}
          onHide={handleDeleteCancel}
          className="modalcustomize confirmationalertmodalDialog"
          bodyClassName="cancelConfirmationbody"
          headerclassName="close_btn_icon"
          title="Confirm Delete"
          footer={false}
          body={
            <ErrorBoundary title="ConfirmationAlert Error">
              <ConfirmationAlert
                BodyFirstContent="Are you sure you want to delete this booking?"
                confirmationButtonContent={deleteLoading ? "Deleting..." : "Delete"}
                cancelButtonContent="Cancel"
                deleteConfirmButton={confirmDelete}
                deleteCancleButton={handleDeleteCancel}
                confirmationButtonDisabled={deleteLoading}
              />
            </ErrorBoundary>
          }
        />
      </Container>
    </ErrorBoundary>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials
})

export default connect(mapStateToProps)(BookingView)